import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { trackEvent, EventName } from 'shared/lib/eventTracking';
import CoralVisualisation from '../CoralVisualisation/CoralVisualisation';
import { useRecentContributions } from '../../hooks/useRecentContributions';

const RecentCoralVisualisation: React.FC = () => {
  const { coralData, isLoading, error } = useRecentContributions();
  const [currentCoralIndices, setCurrentCoralIndices] = useState<number[]>([]);

  useEffect(() => {
    if (coralData.length > 1) {
      const getRandomIndices = () => {
        const newIndices: number[] = [];
        const availableIndices = coralData.map((_, index) => index)
          .filter(index => !currentCoralIndices.includes(index));

        for (let i = 0; i < 2; i++) {
          if (availableIndices.length === 0) break;
          const randomIndex = Math.floor(Math.random() * availableIndices.length);
          newIndices.push(availableIndices[randomIndex]);
          availableIndices.splice(randomIndex, 1);
        }

        return newIndices;
      };

      const refreshCorals = () => {
        const newIndices = getRandomIndices();
        if (newIndices.length === 2) {
          setCurrentCoralIndices(newIndices);
        }
      };

      // Initial random selection
      refreshCorals();

      const interval = setInterval(refreshCorals, 5000);

      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coralData]);

  if (isLoading) {
    return <div className='text-custom-gray font-semibold text-xs'>Loading recent corals...</div>;
  }

  if (error) {
    return <div className='text-custom-gray font-semibold text-xs'>{error}</div>;
  }

  if (coralData.length === 0) {
    return null;
  }

  return (
    <div className="bg-white bg-opacity-90 shadow-lg rounded-xl px-4 py-6 max-w-fit mx-auto">
      <div className="inline-block min-w-fit text-center">
        <p className="text-lg sm:text-xl font-nunito text-coral-black font-bold text-center mb-4">
          Recent Corals
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {currentCoralIndices.length === 2 && (
            <>
              <CoralVisualisation coral={coralData[currentCoralIndices[0]]} />
              <div className="hidden lg:block">
                <CoralVisualisation coral={coralData[currentCoralIndices[1]]} />
              </div>
            </>
          )}
        </div>
        <p className="mt-4 text-xs text-gray-400">
          Real corals powering independent music.
        </p>
        <p>
          <Link
            to="/trust#trust-financials"
            onClick={() => trackEvent(EventName.VIEW_ALL_CONTRIBUTIONS_INTERACTION, {})}
            className="mt-1 text-xs text-coral-pink hover:underline focus:outline-none"
          >
            View all coral contributions, ever
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RecentCoralVisualisation;
